import React, { useRef, useState } from "react";
import UsersTable from "../../Components/Users/UsersTable";
import { ToastContainer } from "react-toastify";
import { useDivision } from "../../hooks/useDivision";
import { useRole } from "../../hooks/useRole";
import { motion, AnimatePresence } from "framer-motion";
import { FiFilter, FiX } from "react-icons/fi";
import ExportUsersFile from "../../Components/Users/ExportUsersFile";

export default function Users() {
  const { data: divisionData } = useDivision();
  const { data: roleData } = useRole();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const filterRef = useRef(null);

  const handleClear = () => {
    setSelectedRole("");
    setSelectedDivision("");
    setSelectedStatus("");
    setIsFilterOpen(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedRole) count++;
    if (selectedDivision) count++;
    if (selectedStatus) count++;
    return count;
  };

  const hasActiveFilters = getActiveFiltersCount() > 0;

  const FilterSection = ({ title, children }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-2 mb-2"
    >
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      {children}
    </motion.div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <ToastContainer />
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Users</h2>
        <div className="flex items-center gap-4">
          <ExportUsersFile
            filters={{
              selectedRole,
              selectedDivision,
              selectedStatus,
            }}
          />
          <div className="relative">
            <button
              ref={filterRef}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-blueColor text-white rounded-lg hover:bg-blueColor/90 transition-colors"
            >
              <FiFilter />
              <span>{isFilterOpen ? "Hide Filters" : "Show Filters"}</span>
            </button>
            {hasActiveFilters && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {getActiveFiltersCount()}
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <div className="relative">
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0, y: -20 }}
              animate={{ height: "auto", opacity: 1, y: 0 }}
              exit={{ height: 0, opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="absolute top-0 left-0 right-0 z-10 bg-white rounded-xl shadow-lg"
            >
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Role Section */}
                  <FilterSection title="Role">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                    >
                      <option value="">All Roles</option>
                      {roleData?.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </FilterSection>

                  {/* Division Section */}
                  <FilterSection title="Division">
                    <select
                      value={selectedDivision}
                      onChange={(e) => setSelectedDivision(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                    >
                      <option value="">All Divisions</option>
                      {divisionData?.map((division) => (
                        <option key={division.id} value={division.id}>
                          {division.name}
                        </option>
                      ))}
                    </select>
                  </FilterSection>

                  {/* Status Section */}
                  <FilterSection title="Status">
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                    >
                      <option value="">All Statuses</option>
                      <option value="true">Active</option>
                      <option value="false">Blocked</option>
                    </select>
                  </FilterSection>
                </div>

                {/* Active Filters */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedRole && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Role: {roleData?.find((r) => r.id === selectedRole)?.name}
                      <button onClick={() => setSelectedRole("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedDivision && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Division:{" "}
                      {
                        divisionData?.find((d) => d.id === selectedDivision)
                          ?.name
                      }
                      <button onClick={() => setSelectedDivision("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedStatus && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Status: {selectedStatus === "true" ? "Active" : "Blocked"}
                      <button onClick={() => setSelectedStatus("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                </div>

                {/* Clear Filters Button */}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleClear}
                    className="px-4 py-2 text-white hover:bg-gray-600 bg-gray-500 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <FiX />
                    Clear All Filters
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div>
        <div className="bg-white rounded-lg shadow-lg p-6">
          <UsersTable
            filters={{
              selectedRole,
              selectedDivision,
              selectedStatus,
            }}
          />
        </div>
      </div>
    </div>
  );
}
